import { FieldViewModel } from "../../fields"
import { getFeatureGeometryIfPolygon } from "../../geom"
import { getFeatureArea } from "../../geom/area"
import { defineDirective } from "../../utils/angular"
import { selectMany } from "../../utils/array"
import { isObject } from "../../utils/utils"
import { IMapTopologyGeometry, IMapTopologyService } from "../topo/types"
import { EditMode, IEditContext, IEditMap, IEditZone } from "./types"
import { ol_format, ol_geom } from "@omnia/exports/openlayers";

export function createEditor(): ng.IDirective {

    return defineDirective(MapEditCtrl, "shared/map/edit/mapEdit.html", {
        context: "=",
    });

}

/** @internal */
export class MapEditCtrl {

    static $inject = ["$scope", "OmniaMapTopologyService"];

    static MinimumZoneArea: number = 0.1;

    private _editWatcher: (feature: any) => boolean;
    private _selectionWatcher: (selection: any) => void;

    private _wktFormat: any;

    private map: IEditMap;

    targetField: FieldViewModel;
    targetCropping: FieldViewModel;

    private _ctxt: IEditContext;

    private _opacity: number;

    private _editMode: EditMode;

    public invalidMergeResult: boolean

    private _currentZone: IEditZone;

    private _selectedZones: IEditZone[];

    private _savedZones: ZoneState[] = [];


    constructor(private $scope: ng.IScope, private topo: IMapTopologyService) {
    }

    $onInit() {

        this._editWatcher = (feature: any) => this.onEditedFeatureChanged(feature);
        this._selectionWatcher = (selection: any) => this.onSelectedFeaturesChanged(selection);

        this._wktFormat = new ol_format.WKT();

    }

    get context(): IEditContext {
        return this._ctxt;
    }

    set context(value: IEditContext) {

        this._ctxt = value;
        this.map = (value ? value.map : null);
        this._savedZones = [];

    }

    get zones(): IEditZone[] {
        if (!this._ctxt) return [];
        return this._ctxt.zones;
    }

    canChangeCurrentZone(): boolean {
        return !this.isEditing();
    }

    canStop(): boolean {
        return this.isEditing();
    }

    canSplitUsingPolygons(): boolean {
        return this._ctxt && this._ctxt.enablePolygonDrawingTools;
    }

    isEditing(): boolean {
        return this.isDrawing() || (!!this._editMode);
    }

    isDrawing(): boolean {
        return this.map.getDrawingMode() || this.map.getEditMode();
    }

    isMerging(): boolean {
        return (this._editMode == EditMode.Merge);
    }

    isLayerEmpty(): boolean {
        return !this.zones.length;
    }

    hasMultipleZones(): boolean {
        return this.zones.length > 1;
    }

    canSplit(): boolean {
        return (!this.isLayerEmpty());
    }

    get currentZone(): IEditZone {
        return this._currentZone;
    }

    set currentZone(value: IEditZone) {
        this.setCurrentZone(value, false);
    }

    get selectedZones(): IEditZone[] {
        return this._selectedZones;
    }

    isZoneSelected(zone: IEditZone): boolean {
        return this.selectedZones && (this.selectedZones.indexOf(zone) >= 0);
    }

    toggleZoneSelection(zone: IEditZone) {

        var selected = !this.isZoneSelected(zone);

        if (!selected) {
            if (this._selectedZones) this._selectedZones.remove(z => z == zone);
        }
        else {
            if (this._selectedZones) {
                if (this._selectedZones.indexOf(zone) < 0) this._selectedZones.push(zone);
            }
            else {
                this._selectedZones = [zone];
            }
        }

        this.updateMapSelection();

    }

    selectAllZones() {
        this._selectedZones = this.zones.slice();
        this.updateMapSelection();
    }

    unselectAllZones() {
        this._selectedZones = [];
        this.updateMapSelection();
    }

    reverseZoneSelection() {
        var selection = this.selectedZones || [];
        this._selectedZones = this.zones.filter(z => selection.indexOf(z) < 0);
        this.updateMapSelection();
    }

    private setCurrentEditMode(mode: EditMode) {

        if (this._ctxt && this._ctxt.onEditModeChanged) {
            this._ctxt.onEditModeChanged(mode);
        }

        this._editMode = mode;

    }
    private updateMapSelection() {
        var sz = this._selectedZones ? this._selectedZones.map(z => z.feature) : [];
        this.map.selectFeatures(sz);
    }

    get opacity() {
        return this._opacity;
    }

    set opacity(value: number) {
        this._opacity = value;
        if (this._ctxt) this._ctxt.layer.opacity = value;
    }

    getSelectedZonesCount(): number {

        return this._selectedZones ? this._selectedZones.length : 0;

    }

    private cleanupEditMode() {

        if (this._editMode == EditMode.Merge) {
            this.map.clearSelection();
        }

        this._savedZones = [];

        this._currentZone = null;

        this.setCurrentEditMode(null);

        this.map.autoEdit(null);

        this.map.enableMultipleSelection = false;
        this.invalidMergeResult = false;

        this.map.watchSelection(this._selectionWatcher);

    }

    private cancelCurrent() {

        this.map.cancelEdit();

        if (this._editMode == EditMode.Draw) {
            this._savedZones.forEach(z => z.cancelChanges());
        }

        this.cleanupEditMode();

    }

    private createInitialZone() {

        var g = this._ctxt.outerGeometry;
        if (!g) return;

        var zone = this._ctxt.createNewZoneWithGeometry(g);

        this._currentZone = zone;
        this.map.selectFeatures([this._currentZone.feature]);

    }

    commitCurrent(): boolean {

        if (!this.canCommitCurrent()) return false;

        switch (this._editMode) {

            case EditMode.Merge:
                return this.commitMergeOp();

            case EditMode.Draw:
            case EditMode.Split:
                return this.commitDrawingOp();

            default:
                this.cleanupEditMode();
                return true;

        }

    }


    private commitMergeOp(): boolean {


        var zones = this._selectedZones.map(z => {
            return {
                zone: z,
                area: getFeatureArea(z.feature)
            }
        })
            .sort((z1, z2) => z2.area - z1.area)
            .map(z => z.zone);

        var geometries = zones.map(g => g.feature.getGeometry());

        var r = this.topo.union(geometries);
        if (!r) return false;

        const union = r.getOpenLayersPolygon()

        if (!union) {
            this.invalidMergeResult = true;
            return false;
        }

        this.invalidMergeResult = false;

        var target = zones[0];

        zones.filter(z => z != target).forEach(z => {
            this._ctxt.removeZone(z);
        });

        target.feature.setGeometry(union);

        this.cleanupEditMode();

        return true;

    }

    private commitDrawingOp(): boolean {

        if (this._editMode == EditMode.Draw) {

            this.map.commitEdit();

            this.commitSplit(null);

        }
        else if (this._editMode == EditMode.Split) {

            var feature = this.map.commitEdit();

            if (isObject(feature)) {

                var geom = feature.getGeometry();

                this.map.removeFeatures([feature]);

                this.commitSplit(geom);

            }

        }

        this.cleanupEditMode();

        return true;

    }

    canMergeSelectedZones(): boolean {

        return this._selectedZones && (this._selectedZones.length > 1);

    }

    canCommitCurrent(): boolean {

        if (this.isMerging()) {
            return this.canMergeSelectedZones();
        }

        return this.map.canCommit();

    }

    startDrawing() {

        var zone = this._currentZone;

        if (!this.commitCurrent()) return;

        this.map.unwatchSelection(this._selectionWatcher);
        this.map.clearSelection();

        this.map.autoEdit({ layer: this._ctxt.layer, watcher: this._editWatcher });

        if (zone && zone.feature) {
            this.saveZoneState(zone);
            this.map.startEditFeature(zone.feature, true);
        }

        this.setCurrentEditMode(EditMode.Draw);

    }

    startMerge() {

        if (!this.commitCurrent()) return;

        this.map.enableMultipleSelection = true;
        this.setCurrentEditMode(EditMode.Merge);

    }

    startLineSplit() {
        this.startSplit("LineString");
    }

    startPolygonSplit() {
        this.startSplit("Polygon");
    }

    startCircleSplit() {
        this.startSplit("Circle");
    }

    startBoxSplit() {
        this.startSplit("Box");
    }

    startSquareSplit() {
        this.startSplit("Square");
    }

    startSplit(type: string) {

        if (!this.commitCurrent()) return;

        this.map.startDrawingNewFeature(this._ctxt.layer, type);

        this.setCurrentEditMode(EditMode.Split);

    }


    private commitSplit(limit: ol_geom.Geometry) {

        const zones = this.zones.filter(z => z.feature);
        const geometries: ol_geom.Geometry[] = zones.map(zone => zone.feature.getGeometry());

        // 210913 - Usersnap 2268/2269
        // We must take inner rings into account separately, since they are ignored by the polygonizer

        const innerRings = selectMany(

            geometries.map(g => {

                if (g instanceof ol_geom.Polygon) {
                    return g.getLinearRings().slice(1).map(g => new ol_geom.Polygon([g.getCoordinates()]))
                }

                return []

            })

        )

        const originalZones = zones.map(z => {

            return {
                zone: z,
                geometry: z.feature.getGeometry(),
                initialGeometry: this.getInitialGeometry(z)
            }

        });

        geometries.push(this._ctxt.outerGeometry);
        if (limit) geometries.push(limit);

        let splits = this.topo
            .polygonize(geometries)
            .map(s => {


                if (innerRings.length) {
                    s = this.topo.difference(s, innerRings)
                }

                return s

            })
            .filter(s => !!s)
            .map(s => this.topo.makeValid(s))
            .filter(s => this.checkSplitResult(s))
            .map(s => s.getOpenLayersGeometry())

        if (limit && (limit instanceof ol_geom.Polygon)) {

            splits = this.mergeSplitsForNewZone(splits, limit);

        }

        if (!limit) {

            splits = this.mergeSplitsForEditedZones(splits);

        }

        this.updateZonesAfterSplit(zones, splits);

        var newZones = splits.map(s => this._ctxt.createNewZoneWithGeometry(s));

        this.setNewZoneValuesAfterSplit(originalZones, newZones);

    }



    private mergeSplitsForEditedZones(splits: any[]) {

        var zones = this._savedZones.filter(z => z.hasGeometryChanged()).map(z => z.zone);

        zones.forEach(z => {

            splits = this.mergeSplitsForZone(splits, getFeatureGeometryIfPolygon(z.feature), false);

        });

        return splits;

    }

    private mergeSplitsForNewZone(splits: any[], zoneGeometry: ol_geom.Polygon): any[] {

        return this.mergeSplitsForZone(splits, zoneGeometry, true);

    }

    private mergeSplitsForZone(splits: any[], zoneGeometry: ol_geom.Polygon, newZone: boolean): any[] {

        var zoneSplits = splits.filter(s => {
            var intersection = this.topo.intersection(s, zoneGeometry).getOpenLayersGeometry();
            return this.checkMinimumSplitArea(intersection) && ((!newZone) || (!this.topo.contains(zoneGeometry, s)));
        });

        if (zoneSplits.length) {
            var union = this.topo.union(zoneSplits).getOpenLayersGeometry();
            splits.removeAll(s => zoneSplits.indexOf(s) >= 0);
            splits.push(union);
        }

        return splits;

    }

    private updateZonesAfterSplit(zones: IEditZone[], splits: ol_geom.Geometry[]) {

        zones.forEach(zone => {

            var geometry = zone.feature.getGeometry();

            var zoneSplits = splits.map(s => {

                return {
                    split: s,
                    intersection: this.topo.intersection(geometry, s, true).getOpenLayersGeometryWithArea()
                };

            });

            zoneSplits = zoneSplits.filter(i => i.intersection && i.intersection.getArea && (i.intersection.getArea() >= MapEditCtrl.MinimumZoneArea));

            zoneSplits = zoneSplits.sort((i1, i2) => i2.intersection.getArea() - i1.intersection.getArea());

            if (zoneSplits.length) {

                var largestSplit = zoneSplits[0].split;
                zone.feature.setGeometry(largestSplit);

                splits.remove(s => s == largestSplit);

            }


        });


    }

    private setNewZoneValuesAfterSplit(originalZones: { zone: IEditZone, geometry: any, initialGeometry: any }[], newZones: IEditZone[]) {

        newZones.forEach(z => {

            var geom = z.feature.getGeometry();

            var ozones = originalZones.map(oz => {
                return {
                    zone: oz.zone,
                    initialGeometry: oz.initialGeometry,
                    intersection: this.topo.intersection(geom, oz.geometry).getOpenLayersGeometryWithArea()
                }
            });

            ozones = ozones.filter(i => i.intersection && (i.intersection.getArea() >= MapEditCtrl.MinimumZoneArea));

            ozones = ozones.sort((i1, i2) => {

                var diff = (i2.initialGeometry ? 1 : 0) - (i1.initialGeometry ? 1 : 0);
                if (diff) return diff;

                return i2.intersection.getArea() - i1.intersection.getArea()

            });

            if (ozones.length) {
                z.value = ozones[0].zone.value;
            }

        });

    }

    /**
     * Checks if @split interesects the outer geometry on a large enough area
     *
     * @param split
     */
    private checkSplitResult(split: IMapTopologyGeometry): boolean {

        if (split) {

            const intersection = this.topo.intersection(split, this._ctxt.outerGeometry, true).getOpenLayersGeometry();

            return this.checkMinimumSplitArea(intersection);

        }

        return false;

    }

    private checkMinimumSplitArea(split: any) {

        return (split && split.getArea && (split.getArea() >= MapEditCtrl.MinimumZoneArea));

    }

    private onSelectedFeaturesChanged(features: any[]) {

        this._selectedZones = (features ? features.map(f => this._ctxt.zones.find(z => z.feature == f)).filter(z => !!z) : []);

        this._currentZone = (this._selectedZones.length ? this._selectedZones[this._selectedZones.length - 1] : null);

        this.invalidMergeResult = false;

    }

    private onEditedFeatureChanged(feature: any): boolean {

        var zone = this._ctxt.zones.find(z => z.feature == feature);

        if (this._currentZone != zone) {

            this.setCurrentZone(zone, true);

        }

        return true;

    }

    private setCurrentZone(zone: IEditZone, apply: boolean = false) {

        if ((this._editMode != EditMode.Draw) && zone.feature) {
            this.map.selectFeatures([zone.feature]);
        }

        this._currentZone = zone;

        if (zone) {
            this.saveZoneState(zone);
        }

        if (apply) {
            this.$scope.$applyAsync();
        }

    }


    private saveZoneState(zone: IEditZone) {

        if (this._savedZones.every(z => z.zone != zone)) {
            this._savedZones.push(new ZoneState(zone, this._wktFormat));
        }

    }


    private getInitialGeometry(zone: IEditZone): any {

        var sz = this._savedZones.find(sz => sz.zone == zone);

        if (sz && sz.hasGeometryChanged()) {
            return sz.initialGeometry;
        }

    }

}

class ZoneState {

    initialGeometry: any;
    initialValue: any;

    constructor(private _zone: IEditZone, private _wktFormat: any) {

        if (_zone.feature) {
            this.initialGeometry = _zone.feature.getGeometry().clone();
        }

        this.initialValue = _zone.value;

    }

    get zone(): IEditZone {
        return this._zone;
    }

    hasGeometryChanged(): boolean {

        if (this._wktFormat && this.initialGeometry) {
            var init = this._wktFormat.writeGeometry(this.initialGeometry);
            var current = this._wktFormat.writeGeometry(this._zone.feature.getGeometry());
            return (init != current);
        }

    }

    cancelChanges() {
        this._zone.feature.setGeometry(this.initialGeometry);
        this._zone.value = this.initialValue;
    }

}
